import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import BalanceOverview from './components/BalanceOverview';
import PayoutTrackingTable from './components/PayoutTrackingTable';
import RevenueAnalytics from './components/RevenueAnalytics';
import WithdrawalScheduler from './components/WithdrawalScheduler';
import SettlementCalendar from './components/SettlementCalendar';
import Button from '../../components/ui/Button';
import useProAccess from '../../hooks/useProAccess';

const ProPayoutManagement = () => {
  const { userProfile } = useAuth();
  const { requireProAccess } = useProAccess();
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectedRail, setSelectedRail] = useState('all');
  const [dateRange, setDateRange] = useState('30d');
  const [refreshing, setRefreshing] = useState(false);

  // Check Pro access
  const { hasAccess, component } = requireProAccess();

  // Real-time balance updates
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate refresh
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
    setLastUpdate(new Date());
  };

  const handleExport = () => {
    // Export functionality
    console.log('Exporting payout data...');
  };

  // Show upgrade prompt if no access
  if (!hasAccess) {
    return (
      <MainLayout>
        {component}
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="dashboard-title">Payout Management</h1>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 text-primary rounded-lg">
                <Icon name="Crown" size={16} />
                <span className="text-sm font-semibold">Pro</span>
              </div>
            </div>
            <p className="dashboard-subtitle">
              Track settlements, revenue, and manage withdrawals across all payment rails
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="stat-badge">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Updated {lastUpdate?.toLocaleTimeString()}
              </span>
            </div>
            <Button
              variant="outline"
              iconName="RefreshCw"
              onClick={handleRefresh}
              loading={refreshing}
            >
              Refresh
            </Button>
            <Button
              variant="outline"
              iconName="Download"
              onClick={handleExport}
            >
              Export
            </Button>
          </div>
        </div>

        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
          {/* Balance Overview Section */}
          <BalanceOverview selectedRail={selectedRail} onRailChange={setSelectedRail} />

          {/* Revenue Analytics */}
          <RevenueAnalytics dateRange={dateRange} onDateRangeChange={setDateRange} selectedRail={selectedRail} />

          {/* Payout Tracking Table */}
          <PayoutTrackingTable selectedRail={selectedRail} dateRange={dateRange} />

          {/* Settlement Calendar & Withdrawal Scheduler */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <SettlementCalendar />
            <WithdrawalScheduler />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ProPayoutManagement;